import React, { Component, createRef } from 'react'
import { createPortal } from 'react-dom'

import Context from '#context'

import { removeLS } from '#helper/localStorage'

import { Button } from 'flowbite-react'

const STATUS_COLORS = {
	granted: 'text-green-600 dark:text-green-400',
	denied: 'text-red-600 dark:text-red-400',
	prompt: 'text-gray-600 dark:text-gray-300',
	unsupported: 'text-gray-600 dark:text-gray-300',
	unknown: 'text-gray-600 dark:text-gray-300',
}

const isIOS = () =>
	/iPad|iPhone|iPod/.test(navigator.userAgent) ||
	(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const isInstalledApp = () =>
	window.matchMedia?.('(display-mode: standalone)').matches ||
	window.navigator.standalone === true

const isNativeElement = (tag) =>
	!(document.createElement(tag) instanceof HTMLUnknownElement)

/**
 * Returns the browser's built-in camera permission element, if there is one.
 * Chrome 153+ has <camera>, Chrome 151+ has <usermedia>. Unlike getUserMedia(),
 * a click on these elements can lift a previous "Block" decision in the page itself.
 *
 * @returns {'camera'|'usermedia'|null}
 */
const getNativeCameraTag = () => {
	if (isNativeElement('camera')) return 'camera'
	if (isNativeElement('usermedia')) return 'usermedia'
	return null
}

/**
 * @class
 * @classdesc - Full-screen layer with Chrome's own camera permission button (<camera> or <usermedia>).
 * Chrome disables that button while anything covers, blurs or animates it ("intersection occluded or distorted").
 * The sidebar has a backdrop blur and a fade, so this layer is portalled to <body>, opaque and without effects.
 */
class NativeCameraDialog extends Component {
	static contextType = Context

	ref = createRef()

	componentDidMount() {
		const el = this.ref.current
		if (this.props.tag === 'usermedia') {
			// Video only. The element rejects `audio: false`, leaving audio out is enough.
			try {
				el.setConstraints?.({ video: {} })
			} catch (error) {
				console.error('Camera element constraints: ', error)
			}
		}
		el.addEventListener('track', this.onSuccess)
		el.addEventListener('stream', this.onSuccess)
		el.addEventListener('error', this.onError)
	}

	componentWillUnmount() {
		const el = this.ref.current
		el.removeEventListener('track', this.onSuccess)
		el.removeEventListener('stream', this.onSuccess)
		el.removeEventListener('error', this.onError)
	}

	onSuccess = () => {
		// We only need the permission here, the scanner opens its own stream.
		const el = this.ref.current
		el.track?.stop()
		el.stream?.getTracks().forEach((track) => track.stop())
		this.props.onGranted()
	}

	onError = () => {
		console.error('Camera element error: ', this.ref.current?.error)
	}

	render() {
		const { t } = this.context
		const Tag = this.props.tag
		return createPortal(
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="camera-permission-title"
				className="fixed inset-0 z-[1000] flex flex-col items-center justify-center gap-6 bg-white px-8 text-center dark:bg-gray-900 dark:text-white"
			>
				<h2
					id="camera-permission-title"
					className="text-xl font-semibold"
				>
					{t('all.sidebar.camera.headline')}
				</h2>
				<p className="max-w-sm">{t('all.sidebar.camera.nativeHint')}</p>
				<Tag ref={this.ref} />
				<Button color="gray" onClick={this.props.onClose}>
					{t('all.close')}
				</Button>
			</div>,
			document.body
		)
	}
}

/**
 * @class
 * @classdesc - Shows the camera permission state in the sidebar and lets the user ask for camera access again.
 * On Chrome with the <camera>/<usermedia> element, a blocked camera can be allowed again right in the app.
 * Other browsers do not let a website lift a block, so it shows the steps for the browser settings there.
 * @example
 * <CameraPermission />
 */
export default class CameraPermission extends Component {
	static contextType = Context

	state = {
		status: 'unknown',
		requesting: false,
		reset: false,
		showNative: false,
	}

	nativeTag = getNativeCameraTag()

	permissionStatus = null

	componentDidMount() {
		this.queryPermission()
	}

	componentWillUnmount() {
		if (this.permissionStatus) this.permissionStatus.onchange = null
	}

	queryPermission = async () => {
		if (!navigator.mediaDevices?.getUserMedia) {
			this.setState({ status: 'unsupported' })
			return
		}
		if (!navigator.permissions?.query) return
		try {
			const permissionStatus = await navigator.permissions.query({
				name: 'camera',
			})
			if (this.permissionStatus) this.permissionStatus.onchange = null
			this.permissionStatus = permissionStatus
			permissionStatus.onchange = () =>
				this.setState({ status: permissionStatus.state })
			this.setState({ status: permissionStatus.state })
		} catch {
			// Some browsers (older Safari, Firefox) can't query "camera".
			this.setState({ status: 'unknown' })
		}
	}

	onNativeGranted = () => {
		removeLS('selectedCamera')
		this.setState({ status: 'granted', reset: true, showNative: false })
		this.queryPermission()
	}

	closeNative = () => {
		this.setState({ showNative: false })
		this.queryPermission()
	}

	requestAccess = async () => {
		removeLS('selectedCamera')
		this.setState({ requesting: true, reset: true })
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: true,
				audio: false,
			})
			stream.getTracks().forEach((track) => track.stop())
			this.setState({ status: 'granted' })
		} catch (error) {
			if (
				error?.name === 'NotAllowedError' ||
				error?.name === 'SecurityError'
			) {
				this.setState({ status: 'denied' })
			}
			console.error('Error when requesting camera access: ', error)
		} finally {
			this.setState({ requesting: false })
			this.queryPermission()
		}
	}

	renderSteps() {
		const { t } = this.context
		const platform = isIOS() ? 'ios' : 'android'
		const steps = t(`all.sidebar.camera.steps.${platform}`, {
			returnObjects: true,
		})

		return (
			<div className="mt-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm dark:border-red-700 dark:bg-gray-800">
				<p className="font-semibold">
					{t('all.sidebar.camera.blockedHeadline')}
				</p>
				<ol className="mt-2 list-decimal space-y-1 pl-5">
					{Array.isArray(steps) &&
						steps.map((step) => <li key={step}>{step}</li>)}
				</ol>
				{isInstalledApp() && (
					<p className="mt-2">
						{t('all.sidebar.camera.installedAppHint')}
					</p>
				)}
				<Button className="mt-3 w-full" onClick={this.queryPermission}>
					{t('all.sidebar.camera.checkAgain')}
				</Button>
			</div>
		)
	}

	renderAction() {
		const { t } = this.context
		const { status, requesting } = this.state

		if (status === 'unsupported') return null

		if (status === 'granted') {
			return (
				<Button
					className="mt-2 w-full"
					disabled={requesting}
					onClick={this.requestAccess}
				>
					{t('all.sidebar.camera.resetSetup')}
				</Button>
			)
		}

		if (this.nativeTag) {
			return (
				<>
					<Button
						className="mt-2 w-full"
						onClick={() => this.setState({ showNative: true })}
					>
						{t('all.sidebar.camera.requestAccess')}
					</Button>
					{this.state.showNative && (
						<NativeCameraDialog
							tag={this.nativeTag}
							onGranted={this.onNativeGranted}
							onClose={this.closeNative}
						/>
					)}
				</>
			)
		}

		if (status === 'denied') return this.renderSteps()

		return (
			<Button
				className="mt-2 w-full"
				disabled={requesting}
				onClick={this.requestAccess}
			>
				{t('all.sidebar.camera.requestAccess')}
			</Button>
		)
	}

	render() {
		const { t } = this.context
		const { status, requesting, reset } = this.state

		return (
			<div className="mt-6 w-full">
				<div>
					{t('all.sidebar.camera.headline')}:{' '}
					<span className={`font-semibold ${STATUS_COLORS[status]}`}>
						{t(`all.sidebar.camera.status.${status}`)}
					</span>
				</div>
				{this.renderAction()}
				{reset && status === 'granted' && !requesting && (
					<p className="mt-2 text-sm">
						{t('all.sidebar.camera.resetDone')}
					</p>
				)}
			</div>
		)
	}
}
